import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faBan,
  faTimes,
  faHourglassHalf,
  faQuestion,
  faCalendarAlt,
  faFlagCheckered,
  faClock,
  faUser,
  faBullseye,
  faPaperclip,
  faStickyNote,
  faTrash,
  faSortAmountDown,
  faSortAmountUp,
  faFilter,
  faFileAlt,
  faBell,
  faQuestionCircle,
  faCheckSquare,
  faSquare,
  faBolt
} from '@fortawesome/free-solid-svg-icons';
import actionService from '../../services/actionService';
import riskScoringService from '../../services/riskScoringService';
import ActionDetailModal from '../common/ActionDetailModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

const RESULT_TYPES = ['successful', 'detected', 'blocked', 'failed', 'partial'];

const ActionList = ({ actions, projectId, onActionDeleted, onBulkDelete, onGenerateCampaign, targets = [] }) => {
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const [selectedAction, setSelectedAction] = useState(null);
  const [localActions, setLocalActions] = useState(actions);
  const [sortDesc, setSortDesc] = useState(true);
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelection = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(displayActions.map(a => a._id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    if (!window.confirm(t('campaigns.bulkDeleteConfirm')?.replace('{{count}}', count) || `Delete ${count} actions?`)) return;
    try {
      await actionService.bulkDelete([...selectedIds]);
      toast.success(`${count} ${t('actions.title').toLowerCase()} deleted`);
      const remaining = localActions.filter(a => !selectedIds.has(a._id));
      setLocalActions(remaining);
      setSelectedIds(new Set());
      if (onBulkDelete) onBulkDelete([...selectedIds]);
    } catch (error) {
      toast.error(t('errors.general'));
    }
  };

  const handleGenerateCampaign = async () => {
    try {
      const campaign = await actionService.generateCampaign([...selectedIds]);
      toast.success(`Campaign "${campaign.name}" created with ${selectedIds.size} actions`);
      setSelectedIds(new Set());
      if (onGenerateCampaign) onGenerateCampaign(campaign);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.general'));
    }
  };

  const toggleFilter = (filter) => {
    setActiveFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filter)) newSet.delete(filter);
      else newSet.add(filter);
      return newSet;
    });
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (window.confirm(t('actions.deleteConfirm') !== 'actions.deleteConfirm' ? t('actions.deleteConfirm') : 'Are you sure you want to delete this action?')) {
      try {
        await actionService.deleteAction(id);
        toast.success(t('common.success'));
        const updatedActions = localActions.filter(action => action._id !== id);
        setLocalActions(updatedActions);
        if (onActionDeleted) onActionDeleted(id);
      } catch (error) {
        toast.error(t('errors.general'));
      }
    }
  };

  const getResultColor = (result) => {
    const colors = {
      'successful': darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800',
      'detected': darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      'blocked': darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800',
      'failed': darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800',
      'partial': darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'
    };
    return colors[result] || (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
  };

  const getResultIcon = (result) => {
    return { 'successful': faCheckCircle, 'detected': faExclamationTriangle, 'blocked': faBan, 'failed': faTimes, 'partial': faHourglassHalf }[result] || faQuestion;
  };

  const formatDuration = (startTimestamp, endTimestamp) => {
    if (!endTimestamp) return null;
    const diffMs = new Date(endTimestamp) - new Date(startTimestamp);
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return diffHours > 0 ? `${diffHours}h ${diffMinutes}m` : `${diffMinutes}m`;
  };

  const getTargetInfo = (targetIds) => {
    if (!targetIds || targetIds.length === 0) return null;
    if (targetIds[0] && typeof targetIds[0] === 'object' && targetIds[0].value) return targetIds;
    const targetObjects = targetIds.map(id => {
      const targetId = typeof id === 'object' ? id._id : id;
      return targets.find(t => t._id === targetId);
    }).filter(Boolean);
    return targetObjects.length > 0 ? targetObjects : null;
  };

  // Sorted and filtered actions
  const displayActions = useMemo(() => {
    let filtered = [...localActions];
    if (activeFilters.size > 0) {
      filtered = filtered.filter(a => activeFilters.has(a.result));
    }
    filtered.sort((a, b) => {
      const dateA = new Date(a.startTimestamp);
      const dateB = new Date(b.startTimestamp);
      return sortDesc ? dateB - dateA : dateA - dateB;
    });
    return filtered;
  }, [localActions, sortDesc, activeFilters]);

  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-600';
  const textMuted = darkMode ? 'text-gray-500' : 'text-gray-500';

  if (localActions.length === 0) {
    return (
      <div className={`text-center py-10 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <p className={textSecondary}>{t('actions.noActions') !== 'actions.noActions' ? t('actions.noActions') : 'No actions logged yet.'}</p>
        <Link
          to={`/projects/${projectId}/actions/new`}
          className="mt-2 inline-block text-blue-600 hover:underline"
        >
          {t('projectDetail.logFirstAction') !== 'projectDetail.logFirstAction' ? t('projectDetail.logFirstAction') : 'Log your first action'}
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Controls: Sort + Filter */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        {/* Filter buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium ${textSecondary}`}>
            <FontAwesomeIcon icon={faFilter} className="mr-1" />
            {t('common.filter') !== 'common.filter' ? t('common.filter') : 'Filter'}:
          </span>
          {RESULT_TYPES.map(result => (
            <button
              key={result}
              onClick={() => toggleFilter(result)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                activeFilters.has(result)
                  ? getResultColor(result) + ' ring-2 ring-offset-1 ' + (darkMode ? 'ring-gray-500 ring-offset-gray-800' : 'ring-gray-300 ring-offset-white')
                  : darkMode
                    ? 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              <FontAwesomeIcon icon={getResultIcon(result)} className="mr-1" />
              {t(`actions.${result}`) !== `actions.${result}` ? t(`actions.${result}`) : result.charAt(0).toUpperCase() + result.slice(1)}
            </button>
          ))}
          {activeFilters.size > 0 && (
            <button
              onClick={() => setActiveFilters(new Set())}
              className={`px-2 py-1 text-xs ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}
            >
              <FontAwesomeIcon icon={faTimes} className="mr-1" />
              {t('common.clear') !== 'common.clear' ? t('common.clear') : 'Clear'}
            </button>
          )}
        </div>

        {/* Sort toggle */}
        <button
          onClick={() => setSortDesc(!sortDesc)}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
            darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <FontAwesomeIcon icon={sortDesc ? faSortAmountDown : faSortAmountUp} />
          {sortDesc
            ? (t('common.newest') !== 'common.newest' ? t('common.newest') : 'Newest first')
            : (t('common.oldest') !== 'common.oldest' ? t('common.oldest') : 'Oldest first')
          }
        </button>
      </div>

      {/* Bulk action toolbar */}
      {selectedIds.size > 0 && (
        <div className={`flex items-center gap-3 mb-4 p-3 rounded-lg border ${
          darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
        }`}>
          <span className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
            <FontAwesomeIcon icon={faCheckSquare} className="mr-1" />
            {selectedIds.size} {t('campaigns.selected') || 'selected'}
          </span>
          <button
            onClick={selectedIds.size === displayActions.length ? deselectAll : selectAll}
            className={`text-sm px-2 py-1 rounded ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-200'}`}
          >
            {selectedIds.size === displayActions.length
              ? (t('common.none') || 'Deselect All')
              : (t('campaigns.selectAll') || 'Select All')}
          </button>
          <div className={`border-l h-5 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`} />
          <button
            onClick={handleBulkDelete}
            className="text-sm px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faTrash} />
            {t('campaigns.bulkDelete') || 'Delete Selected'}
          </button>
          <button
            onClick={handleGenerateCampaign}
            className="text-sm px-3 py-1 rounded bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faBolt} />
            {t('campaigns.generateCampaign') || 'Generate Campaign'}
          </button>
        </div>
      )}

      {/* Results count */}
      {activeFilters.size > 0 && (
        <p className={`text-sm mb-3 ${textMuted}`}>
          {displayActions.length} / {localActions.length} {t('dashboard.totalActions').toLowerCase()}
        </p>
      )}

      {/* Action list */}
      <div className="space-y-3">
        {displayActions.map((action) => {
          const actionTargets = getTargetInfo(action.targets);
          const duration = formatDuration(action.startTimestamp, action.endTimestamp);
          const riskAssessment = riskScoringService.calculateActionRisk(action, actionTargets);

          return (
            <div
              key={action._id}
              className={`rounded-lg border transition-shadow ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 hover:shadow-lg hover:shadow-black/20'
                  : 'bg-white border-gray-200 hover:shadow-md'
              }`}
            >
              {/* Compact View - Click opens ActionDetailModal */}
              <div
                className={`p-4 cursor-pointer ${darkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}`}
                onClick={() => setSelectedAction(action)}
              >
                <div className="flex items-start justify-between">
                  {/* Selection checkbox */}
                  <button
                    onClick={(e) => toggleSelection(action._id, e)}
                    className={`mr-3 mt-1 flex-shrink-0 ${darkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-400 hover:text-blue-600'}`}
                  >
                    <FontAwesomeIcon
                      icon={selectedIds.has(action._id) ? faCheckSquare : faSquare}
                      className={selectedIds.has(action._id) ? (darkMode ? 'text-blue-400' : 'text-blue-600') : ''}
                    />
                  </button>
                  <div className="flex-1">
                    {/* Title Row */}
                    <div className="flex items-center mb-2 flex-wrap gap-2">
                      <h4 className={`font-semibold text-lg ${textPrimary}`}>{action.title}</h4>
                      {action.mitreAttackTechnique && (
                        <span className={`px-2 py-1 text-sm rounded-md font-medium ${darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                          {action.mitreAttackTechnique.id}
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded-md text-sm font-medium flex items-center ${getResultColor(action.result)}`}>
                        <FontAwesomeIcon icon={getResultIcon(action.result)} className="mr-1" />
                        {t(`actions.${action.result}`) !== `actions.${action.result}` ? t(`actions.${action.result}`) : action.result.charAt(0).toUpperCase() + action.result.slice(1)}
                      </span>
                      {riskAssessment.score > 6 && (
                        <span className={`px-2 py-1 rounded-md text-sm font-medium ${
                          riskAssessment.level === 'Critical' ? (darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800') :
                          riskAssessment.level === 'High' ? (darkMode ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-800') :
                          (darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800')
                        }`}>
                          Risk: {riskAssessment.level}
                        </span>
                      )}
                      {action.siemCorrelation?.summary?.detectionVerdict && (
                        <span className={`px-2 py-1 rounded-md text-sm font-medium flex items-center ${
                          action.siemCorrelation.summary.detectionVerdict === 'not_detected' ? (darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800') :
                          action.siemCorrelation.summary.detectionVerdict === 'event_logged' ? (darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800') :
                          action.siemCorrelation.summary.detectionVerdict === 'alert_generated' ? (darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800') :
                          (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')
                        }`}>
                          <FontAwesomeIcon icon={
                            action.siemCorrelation.summary.detectionVerdict === 'not_detected' ? faCheckCircle :
                            action.siemCorrelation.summary.detectionVerdict === 'event_logged' ? faFileAlt :
                            action.siemCorrelation.summary.detectionVerdict === 'alert_generated' ? faBell :
                            faQuestionCircle
                          } className="mr-1 text-xs" />
                          SIEM
                        </span>
                      )}
                    </div>

                    {/* Time and Operator Info */}
                    <div className={`text-sm space-y-1 ${textSecondary}`}>
                      <div className="flex items-center flex-wrap gap-x-4">
                        <span className="flex items-center">
                          <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-primary" />
                          <span className="font-medium">{t('actions.startTime') !== 'actions.startTime' ? t('actions.startTime') : 'Start'}:</span>
                          {format(new Date(action.startTimestamp), 'M/d/yyyy, h:mm:ss a')}
                        </span>
                        {action.endTimestamp && (
                          <>
                            <span className="flex items-center">
                              <FontAwesomeIcon icon={faFlagCheckered} className="mr-1 text-primary" />
                              <span className="font-medium">{t('actions.endTime') !== 'actions.endTime' ? t('actions.endTime') : 'End'}:</span>
                              {format(new Date(action.endTimestamp), 'M/d/yyyy, h:mm:ss a')}
                            </span>
                            {duration && (
                              <span className="font-medium text-blue-600 flex items-center">
                                <FontAwesomeIcon icon={faClock} className="mr-1" />
                                {t('actions.duration') !== 'actions.duration' ? t('actions.duration') : 'Duration'}: {duration}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faUser} className="mr-1 text-primary" />
                        <span className="font-medium">{t('actions.operator')}:</span> {action.operator}
                      </div>
                    </div>

                    {/* Brief Description */}
                    <p className={`mt-2 line-clamp-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{action.description}</p>

                    {/* Quick Stats */}
                    <div className="mt-2 flex items-center gap-4 text-sm">
                      {actionTargets && actionTargets.length > 0 && (
                        <span className="text-orange-600 flex items-center">
                          <FontAwesomeIcon icon={faBullseye} className="mr-1" />
                          {actionTargets.length} target{actionTargets.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {action.evidence && action.evidence.length > 0 && (
                        <span className="text-blue-600 flex items-center">
                          <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
                          {action.evidence.length} {t('actions.evidence')}
                        </span>
                      )}
                      {action.notes && (
                        <span className={`flex items-center ${textMuted}`}>
                          <FontAwesomeIcon icon={faStickyNote} className="mr-1" />
                          {t('actions.notes')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions buttons */}
                  <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                    <button
                      onClick={(e) => handleDelete(action._id, e)}
                      className={`p-2 rounded-md transition-colors ${darkMode ? 'text-red-400 hover:text-red-300 hover:bg-gray-700' : 'text-red-600 hover:text-red-800 hover:bg-red-50'}`}
                      title={t('common.delete')}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Centralized Action Detail Modal */}
      <ActionDetailModal
        action={selectedAction}
        isOpen={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        projectId={projectId}
        onActionUpdated={(updated) => {
          setLocalActions(prev => prev.map(a => a._id === updated._id ? updated : a));
          setSelectedAction(updated);
        }}
      />
    </div>
  );
};

export default ActionList;
